<template>
  <div class="allocation-section">
    <!-- Allocation Mode Selection -->
    <SegmentedControl
      class="mb-3"
      :modelValue="allocationMode"
      :options="allocationModeSegmentOptions"
      :aria-label="$t('production_strategy.allocation_mode_aria')"
      @update:modelValue="setAllocationMode"
    />

    <!-- Whole Production Info -->
    <InfoCallout v-if="allocationMode === 'all'" icon="circle-info">
      {{ $t("portfolio_instructions.whole_production_info") }}
    </InfoCallout>

    <!-- Percentage Allocation -->
    <div v-if="allocationMode === 'percentage'" class="allocation-section__pct">
      <label for="percentage" class="form-label">
        {{ $t("portfolio_instructions.percentage_to_allocate") }}
      </label>
      <div class="allocation-section__pct-input input-group">
        <input
          type="number"
          class="form-control"
          id="percentage"
          :value="percentageInput"
          @input="onPercentageInput($event as InputEvent)"
          @focus="isEditingPercentage = true"
          @blur="onPercentageBlur"
          :class="{ 'is-invalid': percentageHasError }"
          :min="allocationMode === 'percentage' ? 0.01 : 0"
          max="100"
          step="0.01"
          inputmode="decimal"
        />
        <span class="input-group-text">%</span>
      </div>
      <div v-if="percentageHasError" class="text-danger small mt-1">
        <div v-for="error in percentageErrors" :key="error">
          {{ error }}
        </div>
      </div>
    </div>

    <!-- Fixed Amounts Configuration -->
    <div v-if="allocationMode === 'fixed-amounts'" class="mb-3">
      <div class="eyebrow-label mb-2">
        <FontAwesomeIcon icon="calendar-days" />
        {{ $t("portfolio_instructions.period_limits") }}
      </div>

      <!-- Granularity selector -->
      <SegmentedControl
        class="mb-3"
        :modelValue="granularity"
        :options="granularitySegmentOptions"
        :aria-label="$t('portfolio_instructions.granularity')"
        @update:modelValue="requestGranularity"
      />

      <!-- Matrix editor — used for yearly, monthly and quarterly. -->
      <div
        v-if="!contractRangeReady"
        class="alert alert-warning small mb-0"
        role="alert"
      >
        <FontAwesomeIcon icon="exclamation-triangle" class="me-2" />
        {{
          $t("portfolio_instructions.contract_range_required_for_granularity")
        }}
      </div>
      <PeriodLimitMatrix
        v-else
        :modelValue="portfolioInstructions.limits"
        :granularity="granularity"
        :contractStart="contractStart!"
        :contractEnd="contractEnd!"
        :errors="limitsErrors"
        @update:modelValue="updateLimits"
      />
    </div>

    <ConfirmationModalComponent
      class="granularity-switch"
      :visible="pendingGranularity !== null"
      :noHeader="true"
      :confirmButtonText="
        $t('portfolio_instructions.granularity_switch_confirm')
      "
      :confirmButtonIcon="'check'"
      :cancelButtonClass="'btn-ghost'"
      @confirm="confirmGranularitySwitch"
      @cancel="cancelGranularitySwitch"
    >
      <template #body>
        <div class="granularity-switch__body">
          <div class="granularity-switch__header">
            <span class="granularity-switch__icon">
              <FontAwesomeIcon icon="arrows-rotate" />
            </span>
            <div class="granularity-switch__titlegroup">
              <div class="granularity-switch__heading">
                {{ $t("portfolio_instructions.granularity_switch_title") }}
              </div>
              <i18n-t
                keypath="portfolio_instructions.granularity_switch_body"
                tag="p"
                class="granularity-switch__desc"
              >
                <template #from>
                  <strong>{{ granularityLabel(granularity) }}</strong>
                </template>
                <template #to>
                  <strong>{{
                    granularityLabel(pendingGranularity ?? granularity)
                  }}</strong>
                </template>
              </i18n-t>
            </div>
          </div>

          <div class="granularity-switch__panel">
            <div class="granularity-switch__side">
              <div class="eyebrow-label eyebrow-label--sm">
                {{ $t("portfolio_instructions.granularity_switch_from") }}
              </div>
              <div class="granularity-switch__label">
                <FontAwesomeIcon icon="calendar-days" />
                {{ granularityLabel(granularity) }}
              </div>
              <div class="granularity-switch__meta">
                {{
                  $t("portfolio_instructions.granularity_buckets", {
                    count: GRANULARITY_BUCKETS[granularity],
                  })
                }}
              </div>
            </div>
            <FontAwesomeIcon
              icon="arrow-right"
              class="granularity-switch__arrow"
            />
            <div class="granularity-switch__side is-target">
              <div class="eyebrow-label eyebrow-label--sm">
                {{ $t("portfolio_instructions.granularity_switch_to") }}
              </div>
              <div class="granularity-switch__label">
                <FontAwesomeIcon icon="calendar-days" />
                {{ granularityLabel(pendingGranularity ?? granularity) }}
              </div>
              <div class="granularity-switch__meta">
                {{
                  $t("portfolio_instructions.granularity_buckets", {
                    count:
                      GRANULARITY_BUCKETS[pendingGranularity ?? granularity],
                  })
                }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #footerStart>
        <div class="granularity-switch__note">
          <FontAwesomeIcon icon="circle-info" />
          {{ $t("portfolio_instructions.granularity_switch_note") }}
        </div>
      </template>
    </ConfirmationModalComponent>
  </div>
</template>

<script setup lang="ts">
import ConfirmationModalComponent from "@/components/common/ConfirmationModalComponent.vue";
import InfoCallout from "@/components/common/InfoCallout.vue";
import SegmentedControl, {
  type SegmentedControlOption,
} from "@/components/common/SegmentedControl.vue";
import {
  PERCENTAGE_PRECISION,
  changeLimitGranularity,
  configureAllocationMode,
  determineAllocationMode,
  determineLimitGranularity,
  normalizePercentage,
  parseIsoDate,
  projectLimitsToContractRange,
  sameLimits,
} from "@/dashboard/production_strategy/overview/PPA/ppaPortfolioUtils";
import type {
  LimitGranularity,
  PeriodLimit,
  PortfolioInstructions,
} from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import PeriodLimitMatrix from "./PeriodLimitMatrix.vue";

const GRANULARITY_BUCKETS: Record<LimitGranularity, number> = {
  yearly: 1,
  quarterly: 4,
  monthly: 12,
};

type AllocationModeValue = "all" | "percentage" | "fixed-amounts";

const allocationModeOptions: Array<{
  value: AllocationModeValue;
  icon: string;
  labelKey: string;
}> = [
  {
    value: "all",
    icon: "expand-arrows-alt",
    labelKey: "portfolio_instructions.allocate_all",
  },
  {
    value: "percentage",
    icon: "percentage",
    labelKey: "portfolio_instructions.allocate_percentage",
  },
  {
    value: "fixed-amounts",
    icon: "calendar-alt",
    labelKey: "portfolio_instructions.allocate_fixed_amounts",
  },
];

const granularityOptions: Array<{ value: LimitGranularity; labelKey: string }> =
  [
    { value: "yearly", labelKey: "portfolio_instructions.granularity_yearly" },
    {
      value: "quarterly",
      labelKey: "portfolio_instructions.granularity_quarterly",
    },
    {
      value: "monthly",
      labelKey: "portfolio_instructions.granularity_monthly",
    },
  ];

const props = defineProps<{
  percentageErrors?: string[];
  limitsErrors?: string[];
  contractStart?: string | null;
  contractEnd?: string | null;
}>();

const portfolioInstructions = defineModel<PortfolioInstructions>({
  required: true,
});

const emit = defineEmits<{
  "update:granularity": [value: LimitGranularity];
  "field-touched": [field: string];
}>();

const { t } = useI18n();

const allocationModeSegmentOptions = computed(
  (): SegmentedControlOption<AllocationModeValue>[] =>
    allocationModeOptions.map((option) => ({
      value: option.value,
      label: t(option.labelKey),
      icon: option.icon,
    }))
);

const granularitySegmentOptions = computed(
  (): SegmentedControlOption<LimitGranularity>[] =>
    granularityOptions.map((option) => ({
      value: option.value,
      label: t(option.labelKey),
    }))
);

const granularityLabel = (g: LimitGranularity): string => {
  const match = granularityOptions.find((option) => option.value === g);
  return match ? t(match.labelKey) : "";
};

const allocationMode = computed(() => {
  return determineAllocationMode(
    portfolioInstructions.value.allocate_percentage,
    portfolioInstructions.value.limits
  );
});

const granularity = ref<LimitGranularity>(
  determineLimitGranularity(portfolioInstructions.value.limits)
);

watch(granularity, (value) => emit("update:granularity", value), {
  immediate: true,
});

watch(
  () => portfolioInstructions.value.limits,
  (limits) => {
    if (limits.length > 0) {
      granularity.value = determineLimitGranularity(limits);
    }
  },
  { deep: true }
);

// `<=` mirrors the `afterStart` validator; only strict inversion blocks the matrix.
const contractRangeReady = computed(() => {
  if (!props.contractStart || !props.contractEnd) return false;
  const start = parseIsoDate(props.contractStart);
  const end = parseIsoDate(props.contractEnd);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
    return false;
  return start.getTime() <= end.getTime();
});

// Local input state to allow free typing (e.g., "12", "30.", "30.1") without immediate reformatting
const percentageInput = ref<string>("");
const isEditingPercentage = ref(false);

// Initialize local input from model
const initPercentageInputFromModel = () => {
  const normalized = normalizePercentage(
    portfolioInstructions.value.allocate_percentage
  );
  percentageInput.value =
    normalized === null ? "" : normalized.toFixed(PERCENTAGE_PRECISION);
};
initPercentageInputFromModel();

// Keep local input in sync with external changes when not actively editing
watch(
  () => portfolioInstructions.value.allocate_percentage,
  () => {
    if (!isEditingPercentage.value) {
      initPercentageInputFromModel();
    }
  }
);

// Accept both "." and "," as decimal separators when parsing user input
const parseLocaleDecimal = (raw: string): number | null => {
  if (raw == null) {
    return null;
  }
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }
  const normalized = trimmed.replace(",", ".");
  const parsed = Number.parseFloat(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const percentageHasError = computed(() => {
  return !!(props.percentageErrors && props.percentageErrors.length > 0);
});

const emitInstructions = (patch: Partial<PortfolioInstructions>) => {
  portfolioInstructions.value = { ...portfolioInstructions.value, ...patch };
};

const setAllocationMode = (mode: AllocationModeValue) => {
  const config = configureAllocationMode(
    mode,
    portfolioInstructions.value.allocate_percentage,
    portfolioInstructions.value.limits
  );
  emitInstructions({
    allocate_percentage: config.allocatePercentage,
    limits: config.limits,
  });
};

const updatePercentage = (percentage: number | null | undefined) => {
  emitInstructions({
    allocate_percentage: normalizePercentage(percentage) ?? 0,
  });
};

const onPercentageInput = (event: InputEvent) => {
  const target = event.target as HTMLInputElement;
  const raw = target.value;
  percentageInput.value = raw;
  const parsed = parseLocaleDecimal(raw);
  updatePercentage(parsed);
};

const onPercentageBlur = (event: FocusEvent) => {
  const target = event.target as HTMLInputElement;
  const parsed = parseLocaleDecimal(target.value);
  const normalized = normalizePercentage(parsed);
  percentageInput.value =
    normalized === null ? "" : normalized.toFixed(PERCENTAGE_PRECISION);
  isEditingPercentage.value = false;
  emit("field-touched", "allocate_percentage");
};

const updateLimits = (limits: PeriodLimit[]) => {
  emitInstructions({ limits });
};

const pendingGranularity = ref<LimitGranularity | null>(null);
// Granularity to convert FROM when the user switches to monthly/quarterly
// before the contract range is set. The conversion runs once the range
// becomes available; until then the radios reflect the user's choice but
// limits keep their original shape.
const deferredGranularityFrom = ref<LimitGranularity | null>(null);

const convertLimits = (from: LimitGranularity, to: LimitGranularity) => {
  if (from === to) return;
  updateLimits(
    changeLimitGranularity(
      portfolioInstructions.value.limits,
      from,
      to,
      parseIsoDate(props.contractStart!),
      parseIsoDate(props.contractEnd!)
    )
  );
};

const applyGranularity = (target: LimitGranularity) => {
  const from = granularity.value;
  granularity.value = target;
  if (contractRangeReady.value) convertLimits(from, target);
};

const requestGranularity = (target: LimitGranularity) => {
  if (target === granularity.value) return;

  // Monthly/quarterly need the contract range; defer the conversion until the
  // range is available instead of silently keeping limits in the old shape.
  if (target !== "yearly" && !contractRangeReady.value) {
    if (deferredGranularityFrom.value === null) {
      deferredGranularityFrom.value = granularity.value;
    }
    granularity.value = target;
    return;
  }

  const hasNonZero = portfolioInstructions.value.limits.some(
    (l) => l.limit_mwh > 0
  );
  if (hasNonZero) {
    pendingGranularity.value = target;
    return;
  }

  applyGranularity(target);
};

// Run a deferred conversion once the contract range becomes available.
watch(contractRangeReady, (ready) => {
  if (!ready || deferredGranularityFrom.value === null) return;
  const from = deferredGranularityFrom.value;
  deferredGranularityFrom.value = null;
  convertLimits(from, granularity.value);
});

// Keep the flat limits[] aligned with the contract range: when the user moves
// start/end dates, project existing limits through the new boundary shape so
// new partial cells appear (with 0 MWh, so the "must be > 0" validator fires)
// and old out-of-range cells drop out (preventing silent backend rejects).
// Applies to every granularity — yearly now also renders through
// PeriodLimitMatrix (driven by contractStart/contractEnd), so skipping
// yearly here would leave stale out-of-range years on `limits[]`.
const shouldReprojectOnDateChange = computed(
  () =>
    contractRangeReady.value &&
    portfolioInstructions.value.limits.length > 0 &&
    deferredGranularityFrom.value === null
);

// `immediate: true` is load-bearing: the wizard unmounts AllocationSection
// when the user navigates away from step 2, so a mid-edit date change on
// step 1 arrives as the *initial* prop value on remount — without immediate
// firing, the watcher never runs and stale limits get silently saved.
watch(
  () => [props.contractStart, props.contractEnd] as const,
  () => {
    if (!shouldReprojectOnDateChange.value) return;
    const start = parseIsoDate(props.contractStart!);
    const end = parseIsoDate(props.contractEnd!);
    // Skip while the user is mid-edit with an inverted/empty range.
    if (start >= end) return;
    const reprojected = projectLimitsToContractRange(
      portfolioInstructions.value.limits,
      granularity.value,
      start,
      end
    );
    if (!sameLimits(reprojected, portfolioInstructions.value.limits)) {
      updateLimits(reprojected);
    }
  },
  { immediate: true }
);

const confirmGranularitySwitch = () => {
  const target = pendingGranularity.value;
  pendingGranularity.value = null;
  if (target !== null) {
    applyGranularity(target);
  }
};

const cancelGranularitySwitch = () => {
  pendingGranularity.value = null;
};
</script>

<style scoped lang="scss">
.allocation-section__pct-input {
  max-width: 240px;
}

// Granularity switch confirmation — rendered via ConfirmationModalComponent
// with `noHeader`, so the body slot owns all the chrome (icon / heading /
// desc / from→to panel). The styles are reachable from here thanks to the
// class="granularity-switch" on the shared modal.
.granularity-switch :deep(.modal-dialog) {
  --phoenix-modal-width: 560px;
  max-width: 560px;
}

.granularity-switch :deep(.modal-body) {
  padding: 24px 28px 0;
}

.granularity-switch :deep(.modal-footer) {
  padding: 18px 28px 22px;
  border-top: 0;
}

.granularity-switch__body {
  // container is intentionally bare — children own their own spacing
}

.granularity-switch__header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.granularity-switch__icon {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: var(--phoenix-warning-bg-subtle);
  color: var(--phoenix-warning);
  display: grid;
  place-items: center;
  font-size: 18px;
}

.granularity-switch__titlegroup {
  min-width: 0;
  flex: 1;
}

.granularity-switch__heading {
  font-size: 17px;
  font-weight: 700;
  color: var(--phoenix-gray-1000);
  line-height: 1.3;
  margin-bottom: 6px;
}

.granularity-switch__desc {
  margin: 0;
  font-size: 14px;
  color: var(--phoenix-gray-600);
  line-height: 1.5;

  :deep(strong) {
    color: var(--phoenix-gray-900);
    font-weight: 700;
  }
}

.granularity-switch__panel {
  margin-left: 60px;
  margin-top: 16px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 16px;
  align-items: center;
  padding: 14px 16px;
  background: var(--phoenix-gray-50);
  border: 1px solid var(--phoenix-border-color-translucent);
  border-radius: 10px;
}

.granularity-switch__side {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.granularity-switch__label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: var(--phoenix-gray-1000);

  .granularity-switch__side.is-target & {
    color: var(--phoenix-primary);
  }
}

.granularity-switch__meta {
  font-size: 12px;
  color: var(--phoenix-gray-500);
}

.granularity-switch__arrow {
  color: var(--phoenix-gray-400);
  font-size: 14px;
}

.granularity-switch__note {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--phoenix-gray-500);
}
</style>
